import { MutationHandlingServiceV2 } from '@akebono/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { Observable, Subject } from 'rxjs';
import { first, shareReplay, takeUntil } from 'rxjs/operators';

import { validateAllFormFields } from '../../../../../const';
import { LotShareService } from '../../../../../services/lot-share.service';
import { Lot, LotSource, LotTypeEnum } from '../../../../graphql/service/graphql-auto-main-service';
import {
  Agent,
  RequestPhotoFromAuctionByAdminGQL,
} from '../../../../graphql/service/graphql-shared-familiar-service';

@Component({
  selector: 'app-lot-car-inspection-admin-form-modal',
  templateUrl: './lot-add-car-inspection-admin-form-modal.component.html',
  styleUrls: ['./lot-add-car-inspection-admin-form-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LotAddCarInspectionAdminFormModalComponent implements OnInit, OnDestroy {
  @Input() lot: Lot;
  @Input() lotType: LotTypeEnum;
  @Input() inspectionId: number;
  @Input() lotSource: LotSource;
  @Input() lotShared: EventEmitter<void>;

  form: FormGroup;

  agent: Agent;
  commentLimit = 300;
  lotLink$: Observable<string>;

  loading$: Observable<boolean>;
  creating$: Observable<boolean>;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private modal: NzModalRef,
    private mhs: MutationHandlingServiceV2,
    private lotShareService: LotShareService,
    private requestPhotoFromAuctionByAdminGQL: RequestPhotoFromAuctionByAdminGQL,
  ) {
    this.form = this.fb.group({
      agentId: [null, Validators.required],
      comment: ['', [Validators.maxLength(this.commentLimit)]],
    });
  }

  ngOnInit(): void {
    const shareLotResult = this.lotShareService.share(this.lot, this.lotType, this.lotSource, {
      useFullDomain: false,
    });

    this.lotLink$ = shareLotResult.link$.pipe(shareReplay(1));

    if (!this.lotShareService.getLink(this.lot)) {
      this.lotLink$.pipe(first(), takeUntil(this.destroy$)).subscribe(() => this.lotShared.emit());
    }

    this.loading$ = shareLotResult.loading$;
  }

  async createRequest(): Promise<void> {
    validateAllFormFields(this.form);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const lotLink = await this.lotLink$.toPromise();
    const mutationRef = this.mhs.mutate(this.requestPhotoFromAuctionByAdminGQL, {
      input: {
        agentId: this.form.value.agentId,
        lotLink,
        lotId: this.lot.id,
        lotSource: this.lotSource,
        lotSubgroup: this.lotType.toString(),
        type: this.inspectionId,
        lotData: `${this.lot.code} - ${this.lot.auctionName}`,
        comment: this.form.value.comment.trim(),
        dueDate: this.lot.auctionDatetime,
      },
    });

    this.creating$ = mutationRef.loading;
    mutationRef.data.pipe(first(), takeUntil(this.destroy$)).subscribe(() => this.close());
  }

  computeLength = (value: string): number => {
    return (value || '').trim().length;
  };

  close(): void {
    this.modal.destroy();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
